import React, { useEffect } from "react";
import {
  IonCard,
  IonCardContent,
  IonLabel,
  IonBadge,
  IonList,
  IonSegment,
  IonSegmentButton,
  IonLoading,
  IonButton,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
} from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { useIonRouter } from "@ionic/react";
import { format } from "date-fns";
import api from "@/services/api";
import { useI18n } from "@/i18n";

interface Match {
  id: number;
  home_team_name: string;
  away_team_name: string;
  home_score: number | null;
  away_score: number | null;
  kickoff_time: string;
  status: string;
  season_id: number;
  matchday: number;
  prediction?: {
    predicted_home_score: number;
    predicted_away_score: number;
  };
}

interface MatchesResponse {
  matches: Match[];
  count: number;
}

const Matches: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const ionRouter = useIonRouter();
  const { t } = useI18n();
  const [segment, setSegment] = React.useState("matchdays");
  const [localMatches, setLocalMatches] = React.useState<MatchesResponse | null>(null);
  const [seasonId, setSeasonId] = React.useState<number | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab && ["matchdays", "upcoming", "all"].includes(tab)) setSegment(tab);
  }, [location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const tab = new URLSearchParams(location.search).get("tab");
      if (tab && ["matchdays", "upcoming", "all"].includes(tab)) setSegment(tab);
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setIsLoading(true);
        let activeSeasonId: number | null = null;
        try {
          const current = await api.get("/api/matchdays/current");
          activeSeasonId = current.data?.season?.id ?? null;
        } catch {
          activeSeasonId = null;
        }

        const matchesUrl = activeSeasonId
          ? `/api/matches?season=${activeSeasonId}`
          : "/api/matches";
        const response = await api.get(matchesUrl);
        const loadedMatches: Match[] = response.data?.matches || [];
        if (!activeSeasonId && loadedMatches.length > 0) {
          const upcoming = loadedMatches.find(
            (m) => new Date(m.kickoff_time) > new Date() && m.status === "scheduled"
          );
          activeSeasonId = upcoming?.season_id ?? loadedMatches[loadedMatches.length - 1]?.season_id ?? null;
        }

        setSeasonId(activeSeasonId);
        setLocalMatches(response.data);
        setError(null);
      } catch (err) {
        setError(err as Error);
        setLocalMatches({
          matches: [
            { id: 1, home_team_name: "Bayern München", away_team_name: "Borussia Dortmund", home_score: null, away_score: null, kickoff_time: "2024-01-15T20:30:00Z", status: "scheduled", season_id: 1, matchday: 1 },
            { id: 2, home_team_name: "RB Leipzig", away_team_name: "Bayer Leverkusen", home_score: 2, away_score: 1, kickoff_time: "2024-01-10T18:30:00Z", status: "finished", season_id: 1, matchday: 1 },
          ],
          count: 2,
        });
        setSeasonId(1);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMatches();
  }, []);

  const matches = localMatches;

  const fmtDate = (d: string) => {
    try { const dt = new Date(d); return isNaN(dt.getTime()) ? t("invalidDate") : format(dt, "MMM dd, yyyy HH:mm"); }
    catch { return t("invalidDate"); }
  };

  const getStatusColor = (s: string) => { switch (s) { case "finished": return "success"; case "in_progress": return "warning"; case "scheduled": return "primary"; default: return "medium"; } };

  const getFilteredMatches = () => {
    if (!matches?.matches) return [];
    if (segment === "upcoming") {
      const now = new Date();
      return matches.matches.filter((m) => new Date(m.kickoff_time) > now && m.status === "scheduled");
    }
    return matches.matches;
  };

  const navigateToMatchday = (url: string) => {
    ionRouter.push(url);
    setTimeout(() => history.push(url), 50);
    setTimeout(() => { if (window.location.pathname === "/matches") window.location.href = url; }, 200);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("matchesPageTitle")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: "12px 16px" }}>
          <IonSegment value={segment} onIonChange={(e) => setSegment(e.detail.value as string)} style={{ marginBottom: "12px" }}>
            <IonSegmentButton value="matchdays"><IonLabel>{t("matchdaysSegment")}</IonLabel></IonSegmentButton>
            <IonSegmentButton value="upcoming"><IonLabel>{t("upcomingSegment")}</IonLabel></IonSegmentButton>
            <IonSegmentButton value="all"><IonLabel>{t("allMatchesSegment")}</IonLabel></IonSegmentButton>
          </IonSegment>

          {isLoading || !matches ? (
            <IonLoading isOpen message={t("loadingMatches")} />
          ) : segment === "matchdays" ? (
            <>
              <p style={{ margin: "0 0 12px", color: "var(--ion-color-medium)", fontSize: "13px" }}>
                {t("matchdaysCardIntro")}
              </p>
              <IonGrid style={{ padding: 0 }}>
                <IonRow>
                  {Array.from({ length: 34 }, (_, i) => i + 1).map((md) => (
                    <IonCol size="4" key={md} style={{ padding: "4px" }}>
                      <IonButton
                        expand="block"
                        fill="outline"
                        className="matchday-grid-btn"
                        disabled={!seasonId}
                        onClick={() =>
                          seasonId &&
                          navigateToMatchday(`/matchday/${seasonId}/${md}`)
                        }
                        style={{ margin: 0 }}
                      >
                        {md}
                      </IonButton>
                    </IonCol>
                  ))}
                </IonRow>
              </IonGrid>
            </>
          ) : (
            <IonList style={{ padding: 0 }}>
              {(() => {
                const filtered = getFilteredMatches();
                return filtered.length > 0 ? (
                  <>
                    {error && (
                      <IonCard style={{ margin: "0 0 10px" }}><IonCardContent><p style={{ fontSize: "13px", color: "var(--ion-color-medium)" }}>{t("sampleDataNote")}</p></IonCardContent></IonCard>
                    )}
                    {filtered.map((match) => (
                      <IonCard key={match.id} style={{ margin: "0 0 8px" }}>
                        <IonCardContent style={{ padding: "12px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                            <strong style={{ fontSize: "14px" }}>{match.home_team_name} vs {match.away_team_name}</strong>
                            <IonBadge color={getStatusColor(match.status)} style={{ fontSize: "10px" }}>{match.status}</IonBadge>
                          </div>
                          <p style={{ margin: "0 0 6px", fontSize: "12px", color: "var(--ion-color-medium)" }}>
                            {fmtDate(match.kickoff_time)} · {t("matchdayLabel")} {match.matchday}
                          </p>
                          {match.home_score !== null && match.away_score !== null && (
                            <p className="tabular-nums" style={{ margin: "0 0 6px", fontWeight: 700 }}>
                              {match.home_team_name} {match.home_score} : {match.away_score} {match.away_team_name}
                            </p>
                          )}
                          {match.prediction && (
                            <p style={{ margin: 0, fontSize: "12px", color: "var(--ion-color-medium)" }}>
                              {t("yourPrediction")}: {match.prediction.predicted_home_score} : {match.prediction.predicted_away_score}
                            </p>
                          )}
                          <IonButton
                            expand="block"
                            fill="clear"
                            size="small"
                            onClick={() => navigateToMatchday(`/matchday/${match.season_id}/${match.matchday}`)}
                            style={{ marginTop: "4px", "--color": "var(--ion-color-primary)" }}
                          >
                            {t("makePredictionsForMatchday")} {match.matchday}
                          </IonButton>
                        </IonCardContent>
                      </IonCard>
                    ))}
                  </>
                ) : (
                  <IonCard><IonCardContent><p>{segment === "upcoming" ? t("noUpcomingMatches") : t("noMatches")}</p></IonCardContent></IonCard>
                );
              })()}
            </IonList>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Matches;
